import React, { useState } from 'react';
import { UserPlus, X, Shield, Save, Lock, Mail, User, CheckCircle2, Eye, EyeOff } from 'lucide-react';
import { GroupWithPrograms } from '../../services/userManagementService';
import { adminAuthService } from '../../services/adminAuthService';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  availableGroups?: GroupWithPrograms[];
  onUserCreated: () => void;
}

export const NovoUsuarioModal: React.FC<Props> = ({
  isOpen,
  onClose,
  availableGroups = [],
  onUserCreated
}) => {
  const [name, setName] = useState('');
  const [login, setLogin] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<number>(2);
  const [status, setStatus] = useState<'Y' | 'N'>('Y');
  
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [sucesso, setSucesso] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setErro('Nome é obrigatório.');
      return;
    }

    const calculatedLogin = login.trim() || email.split('@')[0] || name.toLowerCase().replace(/\s+/g, '.');

    if (password && password.length < 6) {
      setErro('A senha de acesso deve conter no mínimo 6 caracteres.');
      return;
    }

    setSalvando(true);
    setErro(null);
    setSucesso(null);

    try {
      const res = await adminAuthService.criarUsuario({
        name: name.trim(),
        login: calculatedLogin.toLowerCase().trim(),
        email: email.trim() || `${calculatedLogin}@voopetrans.com.br`,
        password: password.trim() || 'Voope@2025',
        groupIds: [selectedGroup],
        active: status
      });

      if (res.success) {
        setSucesso('Usuário criado com sucesso no Supabase Auth!');
        setTimeout(() => {
          onUserCreated();
          onClose();
        }, 600);
      } else {
        setErro(res.message || 'Não foi possível cadastrar o usuário no sistema.');
      }
    } catch (err: any) {
      setErro(err.message || 'Erro ao cadastrar usuário.');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-xs flex items-center justify-center p-2 sm:p-4 md:p-6 overflow-y-auto">
      <div className="bg-slate-900 border border-slate-700/80 rounded-2xl sm:rounded-3xl w-full max-w-lg max-h-[94vh] flex flex-col shadow-2xl overflow-hidden text-left animate-in fade-in zoom-in-95 duration-150 my-auto">
        
        {/* Cabeçalho */}
        <div className="p-4 sm:p-5 border-b border-slate-800 flex items-center justify-between bg-slate-950/70 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-red-600/20 text-red-400 flex items-center justify-center border border-red-500/30 shrink-0">
              <UserPlus className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-sm sm:text-base font-black text-white">Cadastrar Usuário</h2>
              <p className="text-xs text-slate-400">
                Criação de credenciais Supabase Auth & Permissões
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-white rounded-xl hover:bg-slate-800 transition cursor-pointer shrink-0"
            title="Fechar modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Formulário */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 overflow-hidden">
          <div className="p-4 sm:p-5 space-y-4 overflow-y-auto flex-1 min-h-0">
            {erro && (
              <div className="p-3 rounded-xl bg-rose-950/80 border border-rose-800 text-rose-300 text-xs font-bold">
                {erro}
              </div>
            )}

            {sucesso && (
              <div className="p-3 rounded-xl bg-emerald-950/80 border border-emerald-800 text-emerald-300 text-xs font-bold flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                {sucesso}
              </div>
            )}

          {/* Nome */}
          <div>
            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">
              Nome Completo
            </label>
            <div className="relative">
              <User className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                required
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (!login) {
                    setLogin(e.target.value.toLowerCase().trim().replace(/\s+/g, '.'));
                  }
                }}
                placeholder="Ex: Maria Souza"
                className="w-full pl-9 pr-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:border-red-500 outline-none"
              />
            </div>
          </div>

          {/* Login & E-mail */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">
                Login
              </label>
              <input
                type="text"
                value={login}
                onChange={(e) => setLogin(e.target.value)}
                placeholder="maria.souza"
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:border-red-500 outline-none"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1 flex items-center justify-between">
                <span>E-mail (para login)</span>
                <span className="text-[10px] text-slate-500 font-normal">
                  Recomendado para recuperação de senha
                </span>
              </label>
              <div className="relative">
                <Mail className="w-3.5 h-3.5 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="maria@empresa.com"
                  className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:border-red-500 outline-none"
                />
              </div>
              <p className="text-[10px] text-slate-500 mt-1.5">
                Cadastre um e-mail real para permitir que o usuário recupere sua senha via "Esqueci minha senha".
              </p>
            </div>
          </div>

          {/* Senha */}
          <div>
            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1 flex items-center justify-between">
              <span>Senha de Acesso</span>
              <span className="text-[10px] text-slate-500 font-normal">
                Padrão: Voope@2025
              </span>
            </label>
            <div className="relative">
              <Lock className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Deixe em branco para usar Voope@2025"
                className="w-full pl-9 pr-10 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:border-red-500 outline-none font-mono"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Perfil & Status */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1">
                <Shield className="w-3.5 h-3.5 text-red-400" />
                Perfil / Grupo
              </label>
              <select
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(Number(e.target.value))}
                className="w-full px-3 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:border-red-500 outline-none"
              >
                {availableGroups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">
                Status
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as 'Y' | 'N')}
                className="w-full px-3 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:border-red-500 outline-none"
              >
                <option value="Y">Ativo (Permitido)</option>
                <option value="N">Inativo (Bloqueado)</option>
              </select>
            </div>
          </div>

          {status === 'N' && (
            <div className="bg-amber-950/40 border border-amber-500/30 rounded-xl p-3 text-xs text-amber-300 flex items-start gap-2.5">
              <span className="text-base leading-none">⚠️</span>
              <div>
                <strong className="font-semibold block text-amber-200">Aviso de Inativação:</strong>
                O usuário terá o login bloqueado. Ao tentar acessar o sistema, verá uma notificação para entrar em contato com a transportadora para regularizar o acesso.
              </div>
            </div>
          )}

          </div>

          {/* Ações */}
          <div className="p-4 sm:p-5 border-t border-slate-800 bg-slate-950 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2.5 shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 rounded-xl text-xs font-bold text-slate-400 hover:text-white hover:bg-slate-800 transition cursor-pointer text-center"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={salvando}
              className="px-6 py-2.5 rounded-xl bg-red-600 hover:bg-red-500 text-white text-xs font-bold shadow-lg shadow-red-600/30 flex items-center justify-center gap-2 transition disabled:opacity-50 cursor-pointer"
            >
              <Save className="w-4 h-4 shrink-0" />
              <span>{salvando ? 'Criando...' : 'Salvar Usuário'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
